/**
 * Formatare localizată.
 *
 * **Nu există i18n** — tot UI-ul e în română, string-urile stau direct în componente.
 * Dar formatarea rămâne: sume, date, `ro-RO`, `Europe/Bucharest`. Un `toLocaleString()`
 * fără locale explicit depinde de setările browserului operatorului, deci același
 * document ar arăta diferit pe două calculatoare.
 * Vezi docs/wiki/architecture/stack.md#frontend
 */
import { type CurrencyCode, currencyIsoCode, toCurrencyCode } from '@pcrm/shared/money';
import { formatCalendarDate } from '@pcrm/shared/period';

export const LOCALE = 'ro-RO';
export const TIMEZONE = 'Europe/Bucharest';

/**
 * Sumele vin de la server ca **string**, pentru că în bază sunt `DECIMAL`.
 * Conversia la `number` se face doar aici, la afișare, și niciodată pentru calcule.
 * Vezi docs/wiki/architecture/mariadb-notes.md#bani
 *
 * Tabelul de monede vine din `@pcrm/shared/money`, același pe care îl validează contractul
 * de pe server: două liste ar fi divergat la prima monedă adăugată.
 */
export function formatMoney(amount: string, currencyCode = 1): string {
  const currency: CurrencyCode = toCurrencyCode(currencyCode);
  return new Intl.NumberFormat(LOCALE, {
    style: 'currency',
    currency: currencyIsoCode(currency),
  }).format(Number(amount));
}

/**
 * Dată pur calendaristică (`YYYY-MM-DD`), fără fus orar: nu trece prin `Date`.
 *
 * Aceeași funcție care tipărește data pe contract (#164). Scrisă a doua oară aici, ecranul
 * și actul ar fi putut arăta două forme ale aceleiași zile — iar operatorul ar fi crezut
 * ce vede pe ecran.
 */
export function formatDate(isoDate: string): string {
  return formatCalendarDate(isoDate);
}

/** Moment în timp: stocat în UTC, afișat la ora Bucureștiului. */
export function formatDateTime(isoDateTime: string): string {
  return new Intl.DateTimeFormat(LOCALE, {
    dateStyle: 'short',
    timeStyle: 'short',
    timeZone: TIMEZONE,
  }).format(new Date(isoDateTime));
}
